import { Component, Output, EventEmitter, ViewChild, ElementRef, Input } from '@angular/core';
import { CurrentUserService } from '../shared/services/current-user.service';
import { CurrentUser } from '../models/current-user';
import { AccountSettings } from '../models/account-settings';
import * as EmailValidator from 'email-validator';
import { SearchService } from '../shared/services/search.service';
import { EditionService } from '../shared/services/edition.service';
import { EditionListService } from '../editions/edition.service';
import { PermissionsService } from '../shared/services/permissions.service';
import { ActivatedRoute, Router } from '@angular/router';

// RxJS interfaces
import { Subject } from 'rxjs/Subject';
import { ISubscription } from 'rxjs/Subscription';

@Component({
  selector: 'app-global-user',
  templateUrl: './global-user.component.html',
  styleUrls: ['./global-user.component.less']
})
export class GlobalUserComponent {
  private searchUpdated: Subject<string> = new Subject<string>();
  private searchString: string;
  private rawSearchString: string;
  private searchLoaded = false;
  public currentId: number;
  public accountSettings = false;
  public userAccount: AccountSettings;
  public avatar: string;
  private searchQuerySub: ISubscription;
  public editionId: number;
  public editionName: string;
  public editionColor: string;
  public canSwitchEditions: boolean;
  public canSeeProgramCMS: boolean;
  public canSeeQuickLaunch: boolean;
  private permissionsString: string;
  private hasSamlProviders: boolean;

  @Input() user: CurrentUser;

  @Output() searchStringChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() searchResultsChanged: EventEmitter<Object[]> = new EventEmitter<Object[]>();
  @Output() emailChanged: EventEmitter<String> = new EventEmitter<String>();
  @ViewChild('textInput') textInput: ElementRef;

  constructor(
    private current: CurrentUserService,
    private search: SearchService,
    private editionService: EditionService,
    private editionListService: EditionListService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private permissionsService: PermissionsService,
  ) {

    this.permissionsService.retrievePermissions().subscribe(permissions => {
      this.canSeeProgramCMS = permissions.editProgramContent || permissions.nomadicAdministration;
      this.canSeeQuickLaunch = permissions.editionAdministration;
      this.permissionsString = JSON.stringify(permissions);
    });

    this.searchUpdated.debounceTime(300).subscribe(query => {
      this.handleSearchQueryChanged(query);

      // Check to see if it's an email so we can offer to create a new account
      if (EmailValidator.validate(query)) {
        this.emailChanged.emit(query);
      } else {
        this.emailChanged.emit(null);
      }

      // Manually sync url history to match search string
      const newUrlWithFragment = window.location.pathname + '#' + query;
      if (window.location.hash) {
        window.location.replace(newUrlWithFragment);
      } else {
        window.location.assign(newUrlWithFragment);
      }
    });

    current.getUserData().subscribe(user => {
      this.canSwitchEditions = user.has_multiple_editions;
      this.hasSamlProviders = user.edition.has_saml_providers;
    });

    this.editionListService.waitForEdition()
        .subscribe(edition => {
          this.editionId = edition.id;
          this.editionName = edition.name;
          this.editionColor = '#' + edition.primary_color;
        });

    this.activatedRoute.fragment
        .subscribe( fragment => {
            if (fragment === null) {
                if (this.searchLoaded === true) {
                    this.clearSearchBox();
                    this.searchLoaded = false;
                }
            } else if (typeof fragment === 'string' && fragment.length > 0) {
                this.handleSearchQueryChanged(fragment);
                this.textInput.nativeElement.value = fragment;
                this.searchStringChanged.emit(fragment);
            }
        });

    const instance = this;
    function handleKeyPress(event) {
      instance.handleQuickLaunchKey(event);
    }
    if (this.canSeeQuickLaunch) {
      window.addEventListener("keypress", handleKeyPress);
    }
  }

  private handleQuickLaunchKey(event) {
    // check if an editable element has focus
    const el = document.activeElement;
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el['isContentEditable']) {
      return;
    }

    if (event.key === "/") {
      event.preventDefault();
      this.showQuickLaunch();
    }
  };

  public showQuickLaunch() {
    if (!this.editionId) {
      console.error("Edition id not loaded yet");
      return false;
    }
    const ql  = document.createElement("quick-launch");
    ql.setAttribute("edition-id", this.editionId.toString());
    ql.setAttribute("permissions", this.permissionsString);
    if (this.hasSamlProviders) {
      ql.setAttribute("has-sso", "true");
    }
    ql.addEventListener("close", () => {
      document.body.removeChild(ql);
    })
    document.body.appendChild(ql);
    return false;
  }

  public handleSearchQueryChanged(query: string) {
    this.searchLoaded = true;
    if (this.searchQuerySub) {this.searchQuerySub.unsubscribe(); };
    if (query.length > 0) {
        this.searchQuerySub = this.search.queryAll(query)
                 .subscribe((res: Object[]) => this.searchResultsChanged.emit(res));
    } else {
      this.searchResultsChanged.emit(null);
    }
  }

  public onSearchType(value: string): void {
    this.searchString = value;
    this.searchStringChanged.emit(value);
    this.searchUpdated.next(value);
  }

  public clearSearchBoxButtonClicked(): void {
    window.location.hash = '';
    this.rawSearchString = '';
    this.clearSearchBox();
  }

  public clearSearchBox(): void {
    this.textInput.nativeElement.value = '';
    this.searchResultsChanged.emit(null);
    this.searchStringChanged.emit(null);
    this.emailChanged.emit(null);
  }

  public linkSelected(e: string): void {
    if (e === 'userSettings') {
      let userName: string = this.user.name.split(' ')[0];
      this.current.getAccountSettings()
                       .subscribe(settings => {
                         this.userAccount = settings;
                         this.accountSettings = true;
                       });
    }
    if (e === 'logout') {
      localStorage.removeItem('id_token');
      sessionStorage.removeItem('analyticsCacheObj');
      window.location.assign('/signout');
    }
  }

  public closeDrawer(): void {
    this.accountSettings = false;
  }

  public gotoLearning(path?: string) {
    this.editionService.jumpToLearning(path);
    return false;
  }

}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/global-user/global-user.component.ts